import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import {
  Languages, Heart, Gem, Sparkles, Plus, Loader2, Lock, Check, Play, Flame,
} from "lucide-react";
import { toast } from "sonner";
import LanguageStartModal from "@/components/LanguageStartModal";
import LanguageLessonModal from "@/components/LanguageLessonModal";

/**
 * LearnLanguage — Iter81.
 *
 * Duolingo-style language-learning page. Layout:
 *   - Sticky header rail with hearts, gems, XP, target-language pill.
 *   - List of units. Each unit is a banner card with its lessons rendered
 *     as a snake-like sequence of circular nodes (the recognisable
 *     Duolingo motif).
 *
 * Lesson node states:
 *   - completed → green check, unlocked
 *   - current   → glowing pulse, "Start" CTA
 *   - locked    → faded grey lock
 *
 * The user can ONLY tap the current lesson or completed lessons (for
 * a retake). Locked lessons show a toast nudging them to finish the
 * current one first.
 */
export default function LearnLanguage() {
  const [courses, setCourses] = useState([]);
  const [active, setActive] = useState(null); // course currently selected
  const [loading, setLoading] = useState(true);
  const [startOpen, setStartOpen] = useState(false);
  const [lessonOpen, setLessonOpen] = useState(false);
  const [lessonTarget, setLessonTarget] = useState({ unit: 0, lesson: 0 });

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lang/progress");
      const list = data?.courses || [];
      setCourses(list);
      if (list.length && !active) setActive(list[0]);
      else if (active) {
        // Refresh active reference in case hearts/xp updated.
        const refreshed = list.find((c) => c.course_id === active.course_id);
        if (refreshed) setActive(refreshed);
      }
      if (!list.length) setStartOpen(true);
    } catch (e) {
      toast.error("Couldn't load your language courses");
    } finally {
      setLoading(false);
    }
  };

  // Run-once on mount. `refresh` is a stable closure; we deliberately
  // skip it in deps to avoid refetch on every render.
  useEffect(() => { refresh(); }, []);

  const openLesson = (unitIdx, lessonIdx) => {
    if (!active) return;
    const completed = (active.completed || []).some(([u, l]) => u === unitIdx && l === lessonIdx);
    const cur_u = active.current_unit;
    const cur_l = active.current_lesson;
    const isCurrent = unitIdx === cur_u && lessonIdx === cur_l;
    if (!completed && !isCurrent) {
      toast.message("Finish the current lesson first");
      return;
    }
    setLessonTarget({ unit: unitIdx, lesson: lessonIdx });
    setLessonOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-white/55 text-sm">
        <Loader2 size={18} className="animate-spin mb-2" />
        Loading language courses…
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="learn-language-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8">
        {/* Header rail */}
        <header className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 grid place-items-center rounded-xl bg-[#00FF9D]/12 border border-[#00FF9D]/35">
              <Languages size={17} className="text-[#9bffcc]" />
            </div>
            <div>
              <div className="label-eyebrow text-[#9bffcc]">Language learning</div>
              <h1 className="font-display text-2xl text-white tracking-tight">Languages</h1>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setStartOpen(true)}
            className="text-[11.5px] btn-mint-outline px-3 py-1.5 rounded-full inline-flex items-center gap-1"
            data-testid="learn-language-add"
          >
            <Plus size={11} /> Add another
          </button>
        </header>

        {/* Course picker pills */}
        {courses.length > 1 && (
          <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="course-pills">
            {courses.map((c) => (
              <button
                key={c.course_id}
                type="button"
                onClick={() => setActive(c)}
                className={`text-[12px] font-display px-3 py-1.5 rounded-full border transition inline-flex items-center gap-1.5 ${
                  active?.course_id === c.course_id
                    ? "border-[#00FF9D]/55 bg-[#00FF9D]/10 text-white"
                    : "border-white/10 bg-white/[0.03] text-white/65 hover:text-white"
                }`}
                data-testid={`course-pill-${c.target}`}
              >
                <span aria-hidden>{c.target_flag}</span>
                <span>{c.target_name}</span>
              </button>
            ))}
          </div>
        )}

        {active && (
          <>
            {/* Stats row */}
            <div className="grid grid-cols-4 gap-2 mb-6" data-testid="lang-stats">
              <StatPill icon={<span className="text-base">{active.target_flag}</span>} label="Learning" value={active.target_name} />
              <StatPill icon={<Heart size={14} className="text-rose-400" fill="currentColor" />} label="Hearts" value={`${active.hearts}/${active.max_hearts}`} />
              <StatPill icon={<Gem size={13} className="text-[#bcd1ff]" />} label="Gems" value={active.gems} />
              <StatPill icon={<Sparkles size={13} className="text-[#9bffcc]" />} label="XP" value={active.total_xp || 0} />
            </div>

            {/* Units */}
            <div className="space-y-6">
              {(active.units || []).map((unit, ui) => (
                <UnitCard
                  key={`unit-${ui}-${unit.title || ui}`}
                  unit={unit}
                  unitIndex={ui}
                  course={active}
                  onLessonClick={openLesson}
                />
              ))}
            </div>
          </>
        )}

        {!active && !loading && (
          <div className="text-center py-16">
            <Languages size={32} className="mx-auto text-white/30 mb-3" />
            <div className="font-display text-lg text-white">No languages yet</div>
            <p className="text-[12.5px] text-white/55 mt-1 mb-4">
              Pick a language and start your first bite-sized lesson.
            </p>
            <button
              onClick={() => setStartOpen(true)}
              className="btn-primary px-5 py-2 rounded-full font-display font-semibold text-sm"
              data-testid="learn-language-empty-cta"
            >
              Start a language
            </button>
          </div>
        )}
      </div>

      <LanguageStartModal
        open={startOpen}
        onOpenChange={setStartOpen}
        onStarted={(course) => { setActive(course); refresh(); }}
      />
      {active && (
        <LanguageLessonModal
          open={lessonOpen}
          onOpenChange={(v) => { setLessonOpen(v); if (!v) refresh(); }}
          courseId={active.course_id}
          unitIndex={lessonTarget.unit}
          lessonIndex={lessonTarget.lesson}
          onCompleted={() => refresh()}
        />
      )}
    </div>
  );
}

function StatPill({ icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-white/45 font-display">
        {icon}<span className="truncate">{label}</span>
      </div>
      <div className="font-display text-[14px] text-white tabular-nums mt-0.5 truncate">{value}</div>
    </div>
  );
}

function UnitCard({ unit, unitIndex, course, onLessonClick }) {
  const completed = course.completed || [];
  const cur_u = course.current_unit;
  const cur_l = course.current_lesson;

  const lessonState = (li) => {
    if (completed.some(([u, l]) => u === unitIndex && l === li)) return "done";
    if (unitIndex < cur_u) return "done"; // shouldn't usually trigger but safety
    if (unitIndex === cur_u && li === cur_l) return "current";
    return "locked";
  };

  const unitDoneCount = unit.lessons.filter((_, li) => lessonState(li) === "done").length;
  const unitProgress = (unitDoneCount / unit.lessons.length) * 100;
  const isUnitActive = unitIndex === cur_u;

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.35 }}
      className={`relative rounded-2xl border overflow-hidden ${
        isUnitActive
          ? "border-[#00FF9D]/30 bg-gradient-to-br from-[#00FF9D]/[0.06] via-white/[0.02] to-[#0044FF]/[0.04]"
          : unitDoneCount === unit.lessons.length
            ? "border-white/15 bg-white/[0.025]"
            : "border-white/8 bg-white/[0.02]"
      }`}
      data-testid={`unit-${unitIndex}`}
    >
      <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="label-eyebrow text-white/45">Unit {unitIndex + 1}</div>
          <h3 className="font-display text-lg sm:text-xl text-white tracking-tight">{unit.title}</h3>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[10.5px] text-white/45 font-mono-oak">{unitDoneCount}/{unit.lessons.length}</div>
          <div className="h-1 w-16 mt-1 bg-white/8 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#00FF9D] to-[#0044FF]"
              style={{ width: `${unitProgress}%` }}
            />
          </div>
        </div>
      </div>

      <div className="px-5 py-6">
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-5">
          {unit.lessons.map((lesson, li) => {
            const state = lessonState(li);
            // Snake offset for visual interest: alternate left/right per row of 5.
            const offsetClass = li % 5 === 1 ? "translate-x-3 sm:translate-x-6"
              : li % 5 === 2 ? "translate-x-6 sm:translate-x-10"
              : li % 5 === 3 ? "translate-x-3 sm:translate-x-6"
              : "";
            return (
              <div key={`lesson-${unitIndex}-${li}`} className={`flex flex-col items-center ${offsetClass}`}>
                <LessonNode
                  state={state}
                  lessonIndex={li}
                  unitIndex={unitIndex}
                  onClick={() => onLessonClick(unitIndex, li)}
                />
                <div className="text-[10px] text-white/35 font-mono-oak mt-1.5">L{li + 1}</div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
}

function LessonNode({ state, lessonIndex, unitIndex, onClick }) {
  const isDone = state === "done";
  const isCurrent = state === "current";
  const isLocked = state === "locked";
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.94 }}
      onClick={onClick}
      disabled={isLocked}
      className={`relative h-14 w-14 sm:h-16 sm:w-16 rounded-full grid place-items-center transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${
        isDone
          ? "bg-gradient-to-br from-[#00FF9D] to-[#00b878] shadow-[0_8px_24px_-8px_rgba(0,255,157,0.55)] text-[#0a1a12]"
          : isCurrent
            ? "bg-gradient-to-br from-[#0044FF] to-[#5D3FD3] shadow-[0_10px_28px_-8px_rgba(93,63,211,0.7)] text-white ring-2 ring-[#9bc7ff]/45 animate-pulse"
            : "bg-white/[0.04] border border-white/8 text-white/30 cursor-not-allowed"
      }`}
      aria-label={`Lesson ${lessonIndex + 1} ${state}`}
      data-testid={`lesson-${unitIndex}-${lessonIndex}`}
    >
      {isDone && <Check size={20} strokeWidth={3} />}
      {isCurrent && <Play size={18} fill="currentColor" />}
      {isLocked && <Lock size={16} />}
    </motion.button>
  );
}
